import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { v4 as uuid } from 'uuid';

import type {
    SupportAttachment,
    SupportCreateMessageResponse,
    SupportCreateTicketResponse,
    SupportCursorPage,
    SupportMessage,
    SupportPerson,
    SupportTicketDetails,
    SupportTicketPage,
    SupportTicketStatus,
    SupportTicketStatusFields,
    SupportTicketStatusGroup,
    SupportTicketStatusList,
    SupportTicketStatusOption,
    SupportTicketSummary,
    SupportTokenResponse,
    SupportUploadResponse,
} from 'chaika-frontend-types/frontend-cms';

import { axios as bffAxios } from '@lib/axios';

const SUPPORT_API_BASE_URL = '/support-api';
const SUPPORT_TOKEN_URL = '/api/support/token';
const TOKEN_EXPIRATION_RESERVE_MS = 30_000;
const INVALID_SUPPORT_GATEWAY_RESPONSE = 'Invalid support gateway response';
const SUPPORT_REQUEST_TIMEOUT_MS = 30_000;
const MAX_MUTATION_RETRIES = 3;
const MAX_STATUS_NAME_LENGTH = 100;

export interface SupportTicketListQuery {
    cursor?: string;
    group?: SupportTicketStatusGroup[];
    status?: string[];
    q?: string;
}

interface UploadAttachmentOptions {
    signal?: AbortSignal;
    onProgress?(progress: number): void;
}

interface CachedToken {
    expiresAt: number;
    value: string;
}

let cachedToken: CachedToken | undefined;
let pendingToken: Promise<string> | undefined;

export class SupportApi {
    public static getFreshAccessToken(): Promise<string | undefined> {
        if (process.env.CMS_ENV === 'local-dev') {
            return Promise.resolve(undefined);
        }

        return requestFreshAccessToken();
    }

    public static async listTickets(
        query: SupportTicketListQuery = {},
        signal?: AbortSignal,
    ): Promise<SupportTicketPage> {
        if (!isSupportGatewayAvailable()) {
            return { items: [], nextCursor: null, totalUnread: 0 };
        }

        const { data } = await requestSupport(config =>
            axios.get<SupportTicketPage>(`${SUPPORT_API_BASE_URL}/tickets`, {
                ...config,
                params: query,
                paramsSerializer: { indexes: null },
                signal,
            }),
        );

        return parseSupportTicketPage(data);
    }

    public static async listTicketStatuses(): Promise<SupportTicketStatusOption[]> {
        if (!isSupportGatewayAvailable()) {
            return [];
        }

        const { data } = await requestSupport(config =>
            axios.get<SupportTicketStatusList>(`${SUPPORT_API_BASE_URL}/ticket-statuses`, config),
        );

        return parseSupportResponse(data, isSupportTicketStatusList).items;
    }

    public static async createTicket(title: string): Promise<SupportCreateTicketResponse> {
        const { data } = await requestSupport(
            config => axios.post<SupportCreateTicketResponse>(`${SUPPORT_API_BASE_URL}/tickets`, { title }, config),
            uuid(),
        );

        return withSupportTicketNumber(parseSupportResponse(data, isSupportCreateTicketResponse));
    }

    public static async getTicket(ticketId: string): Promise<SupportTicketDetails> {
        const { data } = await requestSupport(config =>
            axios.get<SupportTicketDetails>(`${SUPPORT_API_BASE_URL}/tickets/${ticketId}`, config),
        );

        return withSupportTicketNumber(parseSupportResponse(data, isSupportTicketDetails));
    }

    public static async listMessages(ticketId: string, before?: string): Promise<SupportCursorPage<SupportMessage>> {
        const { data } = await requestSupport(config =>
            axios.get<SupportCursorPage<SupportMessage>>(`${SUPPORT_API_BASE_URL}/tickets/${ticketId}/messages`, {
                ...config,
                params: { before },
            }),
        );

        return parseSupportMessagePage(data);
    }

    public static async uploadAttachment(
        file: File,
        { signal, onProgress }: UploadAttachmentOptions = {},
    ): Promise<SupportUploadResponse> {
        const form = new FormData();
        form.append('file', file);
        const { data } = await requestSupport(config =>
            axios.post<SupportUploadResponse>(`${SUPPORT_API_BASE_URL}/uploads`, form, {
                ...config,
                signal,
                onUploadProgress: event => onProgress?.(event.total ? event.loaded / event.total : 0),
            }),
        );

        return parseSupportResponse(data, isSupportUploadResponse);
    }

    public static async createMessage(
        ticketId: string,
        text: string,
        attachmentIds: string[] = [],
    ): Promise<SupportCreateMessageResponse> {
        const payload = { text, attachmentIds: [...attachmentIds] };
        const { data } = await requestSupport(
            config =>
                axios.post<SupportCreateMessageResponse>(
                    `${SUPPORT_API_BASE_URL}/tickets/${ticketId}/messages`,
                    payload,
                    config,
                ),
            uuid(),
        );

        return parseSupportResponse(data, isSupportCreateMessageResponse);
    }

    public static async markTicketAsRead(ticketId: string): Promise<void> {
        await requestSupport(config =>
            axios.post(`${SUPPORT_API_BASE_URL}/tickets/${ticketId}/read`, undefined, config),
        );
    }
}

export function toSupportTicketNumber(value: unknown): number | null {
    if (value === undefined || value === null) {
        return null;
    }
    if (typeof value === 'number' && Number.isInteger(value) && value >= 1) {
        return value;
    }
    console.warn('[SupportApi] Invalid ticket number is treated as unknown', value);

    return null;
}

export function getSupportErrorCode(error: unknown): string | undefined {
    return axios.isAxiosError(error) && typeof error.response?.data?.error?.code === 'string'
        ? error.response.data.error.code
        : undefined;
}

async function requestSupport<T>(
    request: (config: AxiosRequestConfig) => Promise<AxiosResponse<T>>,
    idempotencyKey?: string,
    retryTransient = false,
): Promise<AxiosResponse<T>> {
    let retryCount = 0;
    let hasRefreshedToken = false;
    while (true) {
        const authorization = await getAuthorizationConfig();
        try {
            return await request({
                ...authorization,
                timeout: SUPPORT_REQUEST_TIMEOUT_MS,
                headers: { ...authorization.headers, ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}) },
            });
        } catch (error) {
            if (!axios.isAxiosError(error)) {
                throw error;
            }
            const status = error.response?.status;
            const code = error.response?.data?.error?.code;
            if (status === 401 && !hasRefreshedToken && process.env.CMS_ENV !== 'local-dev') {
                hasRefreshedToken = true;
                cachedToken = undefined;
                await requestFreshAccessToken();
                continue;
            }
            if (status === 409 && code === 'IDEMPOTENCY_KEY_REUSED') {
                console.error('[SupportApi] Idempotency key was reused with a different operation');
            }
            const isInProgress = status === 409 && code === 'IDEMPOTENCY_REQUEST_IN_PROGRESS';
            const isTransient =
                !error.response || status === 429 || (status !== undefined && status >= 500 && status <= 599);
            if (
                (!idempotencyKey && !retryTransient) ||
                (!isInProgress && !isTransient) ||
                retryCount >= MAX_MUTATION_RETRIES
            ) {
                throw error;
            }
            const delay = isInProgress ? 1_500 : 1_000 * 2 ** retryCount;
            retryCount += 1;
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
}

async function getAuthorizationConfig(): Promise<{ headers?: { Authorization: string } }> {
    if (process.env.CMS_ENV === 'local-dev') {
        return {};
    }

    if (cachedToken && cachedToken.expiresAt - TOKEN_EXPIRATION_RESERVE_MS > Date.now()) {
        return { headers: { Authorization: `Bearer ${cachedToken.value}` } };
    }

    const token = await requestFreshAccessToken();

    return { headers: { Authorization: `Bearer ${token}` } };
}

async function requestFreshAccessToken(): Promise<string> {
    pendingToken ??= requestToken().finally(() => {
        pendingToken = undefined;
    });

    return pendingToken;
}

async function requestToken(): Promise<string> {
    const { data } = await bffAxios.get<SupportTokenResponse>(SUPPORT_TOKEN_URL);

    cachedToken = {
        value: data.token,
        expiresAt: Date.now() + data.expiresIn * 1000,
    };

    return data.token;
}

function parseSupportCursorPage<T>(value: unknown, isItem: (item: unknown) => item is T): SupportCursorPage<T> {
    if (
        !isRecord(value) ||
        !Array.isArray(value.items) ||
        !value.items.every(isItem) ||
        !(value.nextCursor === null || typeof value.nextCursor === 'string')
    ) {
        throw new Error(INVALID_SUPPORT_GATEWAY_RESPONSE);
    }

    return value as unknown as SupportCursorPage<T>;
}

function parseSupportTicketPage(value: unknown): SupportTicketPage {
    const page = parseSupportCursorPage(value, isSupportTicketSummary);
    if (!isRecord(value)) {
        throw new Error(INVALID_SUPPORT_GATEWAY_RESPONSE);
    }
    const totalUnread = value.totalUnread === undefined ? 0 : value.totalUnread;
    if (!isNonnegativeInteger(totalUnread)) {
        throw new Error(INVALID_SUPPORT_GATEWAY_RESPONSE);
    }

    return { ...page, items: page.items.map(withSupportTicketNumber), totalUnread };
}

function parseSupportMessagePage(value: unknown): SupportCursorPage<SupportMessage> {
    if (
        !isRecord(value) ||
        !Array.isArray(value.items) ||
        !value.items.every(isSupportMessage) ||
        !(value.nextCursor === null || typeof value.nextCursor === 'string')
    ) {
        throw new Error(INVALID_SUPPORT_GATEWAY_RESPONSE);
    }

    return {
        items: value.items.map(toSupportMessage),
        nextCursor: value.nextCursor,
    };
}

function parseSupportResponse<T>(value: unknown, isResponse: (response: unknown) => response is T): T {
    if (!isResponse(value)) {
        throw new Error(INVALID_SUPPORT_GATEWAY_RESPONSE);
    }

    return value;
}

function withSupportTicketNumber<T extends Record<string, unknown>>(ticket: T): T & { number: number | null } {
    return { ...ticket, number: toSupportTicketNumber(ticket.number) };
}

function isSupportTicketSummary(
    value: unknown,
): value is Record<string, unknown> & Omit<SupportTicketSummary, 'number'> {
    return (
        isRecord(value) &&
        typeof value.id === 'string' &&
        typeof value.title === 'string' &&
        hasSupportTicketStatusFields(value) &&
        (value.responsible === null || isSupportPerson(value.responsible)) &&
        typeof value.unread === 'number' &&
        (value.lastMessageAt === null || typeof value.lastMessageAt === 'string')
    );
}

function isSupportTicketDetails(
    value: unknown,
): value is Record<string, unknown> & Omit<SupportTicketDetails, 'number'> {
    return (
        isRecord(value) &&
        typeof value.id === 'string' &&
        typeof value.title === 'string' &&
        hasSupportTicketStatusFields(value) &&
        (value.responsible === null || isSupportPerson(value.responsible)) &&
        Array.isArray(value.participants) &&
        value.participants.every(isSupportPerson)
    );
}

function isSupportCreateTicketResponse(
    value: unknown,
): value is Record<string, unknown> & Omit<SupportCreateTicketResponse, 'number'> {
    return (
        isRecord(value) &&
        typeof value.id === 'string' &&
        typeof value.title === 'string' &&
        hasSupportTicketStatusFields(value)
    );
}

function hasSupportTicketStatusFields(
    value: Record<string, unknown>,
): value is Record<string, unknown> & SupportTicketStatusFields {
    return (
        isSupportTicketStatus(value.status) &&
        isSupportTicketStatusName(value.statusName) &&
        (value.statusGroup === null || isSupportTicketStatusGroup(value.statusGroup))
    );
}

function isSupportTicketStatusList(value: unknown): value is SupportTicketStatusList {
    return isRecord(value) && Array.isArray(value.items) && value.items.every(isSupportTicketStatusOption);
}

function isSupportTicketStatusOption(value: unknown): value is SupportTicketStatusOption {
    return (
        isRecord(value) &&
        isSupportTicketStatusName(value.name) &&
        isSupportTicketStatusGroup(value.group) &&
        typeof value.color === 'string'
    );
}

function isSupportTicketStatusName(value: unknown): value is string {
    return typeof value === 'string' && value.length > 0 && value.length <= MAX_STATUS_NAME_LENGTH;
}

function isSupportTicketStatusGroup(value: unknown): value is SupportTicketStatusGroup {
    return value === 'to_do' || value === 'in_progress' || value === 'complete';
}

function isSupportMessage(value: unknown): value is Omit<SupportMessage, 'authorId'> & { authorId?: string | null } {
    return (
        isRecord(value) &&
        typeof value.id === 'string' &&
        (value.author === 'doctor' || value.author === 'support') &&
        (value.authorId === undefined || value.authorId === null || typeof value.authorId === 'string') &&
        typeof value.authorName === 'string' &&
        typeof value.text === 'string' &&
        Array.isArray(value.attachments) &&
        value.attachments.every(isSupportAttachment) &&
        typeof value.createdAt === 'string' &&
        (value.editedAt === undefined || typeof value.editedAt === 'string')
    );
}

function toSupportMessage({
    id,
    author,
    authorId,
    authorName,
    text,
    attachments,
    createdAt,
    editedAt,
}: Omit<SupportMessage, 'authorId'> & { authorId?: string | null }): SupportMessage {
    return {
        id,
        author,
        ...(authorId ? { authorId } : {}),
        authorName,
        text,
        attachments,
        createdAt,
        ...(editedAt ? { editedAt } : {}),
    };
}

function isSupportCreateMessageResponse(value: unknown): value is SupportCreateMessageResponse {
    return isRecord(value) && typeof value.id === 'string' && typeof value.createdAt === 'string';
}

function isSupportPerson(value: unknown): value is SupportPerson {
    return isRecord(value) && typeof value.id === 'string' && typeof value.name === 'string';
}

function isSupportAttachment(value: unknown): value is SupportAttachment {
    return (
        isRecord(value) &&
        typeof value.id === 'string' &&
        typeof value.fileName === 'string' &&
        typeof value.mimeType === 'string' &&
        typeof value.size === 'number' &&
        typeof value.url === 'string'
    );
}

function isSupportUploadResponse(value: unknown): value is SupportUploadResponse {
    return isRecord(value) && typeof value.attachmentId === 'string';
}

function isSupportTicketStatus(value: unknown): value is SupportTicketStatus {
    return (
        value === 'new' ||
        value === 'in_progress' ||
        value === 'in_development' ||
        value === 'done' ||
        value === 'deleted'
    );
}

function isNonnegativeInteger(value: unknown): value is number {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

export function isSupportGatewayAvailable(): boolean {
    return process.env.NODE_ENV !== 'development' || process.env.CMS_ENV === 'local-dev';
}
